/**
 * Gender in one place: the value stored on person.gender, the profile-form
 * options, and the label results show for a person or a team.
 */

/** person.gender in the database; null means not set. */
export type Gender = "female" | "male" | "other";
export const GENDER_KEYS = [
  "female",
  "male",
  "other",
] as const satisfies readonly Gender[];

/** Profile-form value: a gender, or "unspecified" (saved as null). */
export type GenderValue = Gender | "unspecified";

export const GENDER_OPTIONS: { value: GenderValue; label: string }[] = [
  { value: "unspecified", label: "Prefer not to say" },
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "other", label: "Other" },
];

export const GENDER_LABELS: Record<GenderValue, string> = {
  unspecified: "Gender not set",
  female: "Female",
  male: "Male",
  other: "Other",
};

/** Label on a result row: a person's own gender, or a team's resolved one. */
export type ResultGender = "Female" | "Male" | "Other" | "Mixed";

const RESULT_LABEL: Record<Gender, ResultGender> = {
  female: "Female",
  male: "Male",
  other: "Other",
};

/** A person's result label. Unset or unknown values get no label. */
export function personResultGender(
  gender: string | null | undefined,
): ResultGender | undefined {
  return (GENDER_KEYS as readonly string[]).includes(gender ?? "")
    ? RESULT_LABEL[gender as Gender]
    : undefined;
}

/**
 * A team's result label from its members' labels:
 * - no members, or nobody has a gender set → no label
 * - everyone set and the same → that gender
 * - otherwise (they differ, or some are set and some missing) → "Mixed"
 */
export function teamResultGender(
  members: readonly (ResultGender | undefined)[],
): ResultGender | undefined {
  const set = members.filter((g): g is ResultGender => !!g);
  if (set.length === 0) return undefined;
  const same = set.every((g) => g === set[0]);
  return same && set.length === members.length ? set[0] : "Mixed";
}
